import type { SpecType } from "@/api/contracts/specifications";
import {
  editorLineName,
  hasRemovedRoomNotes,
  lineDisplayName,
  locationsText,
  selectionRowName,
  type EditorRowKind,
  type LineLocationType,
} from "@/lib/spec-lines-display";

// The changed-from-base highlight's rules — what the info icon says and which
// old values a row draws beside its new ones (#1083; ledger §7, "Changed from
// base widens": #1043's variant E, D1–D6, and #1060 Q5, Q7–Q9). Page-agnostic:
// the details rows and the Editor's rows (#1104) draw them, the push preview
// (#1090) after.
//
// **The shapes are declared here rather than imported from the wire face**, as
// `spec-lines-display.ts` declares its own: the client graph never imports
// `@/server/*`, and each shape is the narrow slice a rule reads.
//
// Every base value comes from the server's per-row facts (ADR-0021 decision
// 3): these rules pick and format them, never match rows or rooms.

/** A line field that can differ from its base line, in the server's order. */
export type LineChangedField =
  "deleted" | "name" | "notes" | "locationType" | "perPlan" | "rooms" | "roomNotes";

/** A selection field that can differ from its base selection. */
export type SelectionChangedField =
  "deleted" | "item" | "name" | "notes" | "paidUpgrade" | "manufacturer";

/** A group field that can differ from its base group. */
export type GroupChangedField = "name" | "notes";

/** A group's facts against its base group: what differs, and the base's values when flagged. */
export interface GroupChangeFacts {
  changedFields: readonly GroupChangedField[];
  base: { name: string; notes: string | null } | null;
}

/** The facts the sentence reads off a line. */
export interface LineChangeFacts {
  deleted: boolean;
  hasBaseRow: boolean;
  changedFields: readonly LineChangedField[];
}

/** The facts the sentence reads off a selection. */
export interface SelectionChangeFacts {
  deleted: boolean;
  hasBaseRow: boolean;
  changedFields: readonly SelectionChangedField[];
}

/** The ancestor a spec's changes are against: a community's Base, else Core. */
export function ancestorName(specType: SpecType): "Base" | "Core" {
  return specType === "community" ? "Base" : "Core";
}

/** A line field's #1043 label — the info icon's words; the push preview's too (#1090). */
export function lineFieldLabel(field: LineChangedField, ancestor: string): string {
  switch (field) {
    case "deleted":
      return `Removed on the ${ancestor}`;
    case "name":
      return "Line name";
    case "notes":
      return "Line notes";
    case "locationType":
      return "Location type";
    case "perPlan":
      return "Per plan";
    case "rooms":
      return "Rooms";
    case "roomNotes":
      return "Room notes";
  }
}

/** A selection field's #1043 label. */
export function selectionFieldLabel(field: SelectionChangedField, ancestor: string): string {
  switch (field) {
    case "deleted":
      return `Removed on the ${ancestor}`;
    case "item":
      return "Item";
    case "name":
      return "Selection name";
    case "notes":
      return "Selection notes";
    case "paidUpgrade":
      return "Paid upgrade";
    case "manufacturer":
      return "Manufacturer";
  }
}

/** A group field's #1043 label. */
export const GROUP_FIELD_LABEL: Record<GroupChangedField, string> = {
  name: "Group name",
  notes: "Group notes",
};

function changedSentence(ancestor: string, labels: readonly string[]): string | null {
  return labels.length === 0 ? null : `Changed from ${ancestor}: ${labels.join(", ")}.`;
}

/**
 * A struck row — its selection, or its line, deleted: the row itself is the
 * change, so it draws no per-field pairs.
 */
export function isStruckRow(
  line: { deleted: boolean },
  selection: { deleted: boolean } | undefined,
): boolean {
  return line.deleted || Boolean(selection?.deleted);
}

/**
 * An added row — a community spec's row whose line, or whose selection, has
 * no base row, which is when today's tint (`isRowChanged`) reads it as one.
 * A line drawn over no selection is judged by the line alone: the donor tints
 * it for its missing based-on selection, but nothing was added, so its own
 * fields still pair, and with none changed the icon says so
 * (`NO_SELECTION_SENTENCE`).
 */
export function isAddedRow(
  line: { hasBaseRow: boolean },
  selection: { hasBaseRow: boolean } | undefined,
  specType: SpecType,
): boolean {
  return (
    specType === "community" &&
    (!line.hasBaseRow || (selection !== undefined && !selection.hasBaseRow))
  );
}

/**
 * The sentence on a row tinted for its missing selection alone — a line with
 * no selection whose own fields match its base line (`isAddedRow`): the
 * owner's words, its ancestor in lower case (disposition D4 = b on PR #1106).
 */
export function noSelectionSentence(specType: SpecType): string {
  return `No selection. The ${ancestorName(specType).toLowerCase()} line has one.`;
}

/**
 * The info icon's sentence on a row, which is also its accessible name:
 * "Removed…" on a struck row, "Added…" on an added one, else "Changed from
 * Base: " and the line's fields then the selection's, in the server's order,
 * or — a line over no selection with no field changed — "No selection…".
 * The item's maker and numbers follow the item and are never named. `null`
 * when the row does not differ.
 */
export function rowChangeSentence(
  line: LineChangeFacts,
  selection: SelectionChangeFacts | undefined,
  specType: SpecType,
): string | null {
  const ancestor = ancestorName(specType);
  if (isStruckRow(line, selection)) return `Removed from this spec. The ${ancestor} still has it.`;
  if (isAddedRow(line, selection, specType)) return `Added in this spec. Not on the ${ancestor}.`;
  const changed = changedSentence(ancestor, [
    ...line.changedFields.map((field) => lineFieldLabel(field, ancestor)),
    ...(selection?.changedFields ?? []).map((field) => selectionFieldLabel(field, ancestor)),
  ]);
  return changed ?? (selection === undefined ? noSelectionSentence(specType) : null);
}

/** A group header's sentence; `null` when the group does not differ. */
export function groupChangeSentence(
  group: Pick<GroupChangeFacts, "changedFields">,
  specType: SpecType,
): string | null {
  return changedSentence(
    ancestorName(specType),
    group.changedFields.map((field) => GROUP_FIELD_LABEL[field]),
  );
}

// ---------------------------------------------------------------------------
// Which old values a row draws beside its new ones.
// ---------------------------------------------------------------------------

/** An old value: its text, or `null` for an empty side, which reads *None*. */
export type OldValue = string | null;

/** The old notes' cut (#1043 brief; the full text stays in `title`). */
export const OLD_NOTES_MAX = 140;

/** "Refer to plan for details." — a per-plan line's item columns (`PerPlanDetails.tsx:19`). */
export const PER_PLAN_TEXT = "Refer to plan for details.";

/** A line turned per-plan's old side, and a cleared paid-upgrade mark's text. */
export const NOT_PER_PLAN_TEXT = "Not per plan";

const BLOCK_TAGS =
  /<\/?(?:p|div|br|li|ul|ol|h[1-6]|blockquote|pre|tr|td|th|table|thead|tbody|hr)\b[^>]*>/gi;

const NAMED_ENTITIES: Record<string, string> = {
  amp: "&",
  lt: "<",
  gt: ">",
  quot: '"',
  apos: "'",
  nbsp: " ",
};

/**
 * Every tag dropped, the pass repeated until nothing changes — the fixed-point
 * shape CodeQL's multi-character sanitization check reads as complete. One
 * pass of this pattern already leaves no `<` with a `>` after it.
 */
function withoutTags(html: string): string {
  let text = html;
  let previous: string;
  do {
    previous = text;
    text = text.replace(/<[^>]*>/g, "");
  } while (text !== previous);
  return text;
}

/**
 * Notes as the old side shows them: the **text** of the sanitized markup —
 * tags dropped, block boundaries as spaces, entities decoded, runs of white
 * space collapsed (#1083 Assumption 7). A cut through markup cannot render
 * safely, and a struck block of rich text would read as content. The result
 * is text, never markup: an escaped `&lt;b&gt;` decodes to a literal `<b>`,
 * which every caller renders as React text.
 */
export function notesText(html: string | null | undefined): string {
  if (!html) return "";
  return withoutTags(html.replace(BLOCK_TAGS, " "))
    .replace(/&(#x[0-9a-f]+|#\d+|[a-z]+);/gi, (entity, body: string) => {
      if (body[0] === "#") {
        const code =
          body[1] === "x" || body[1] === "X"
            ? Number.parseInt(body.slice(2), 16)
            : Number.parseInt(body.slice(1), 10);
        return Number.isNaN(code) ? entity : String.fromCodePoint(code);
      }
      return NAMED_ENTITIES[body.toLowerCase()] ?? entity;
    })
    .replace(/\s+/g, " ")
    .trim();
}

/** Text as an old value: empty is *None*. */
function oldValue(text: string | null | undefined): OldValue {
  const trimmed = text?.trim() ?? "";
  return trimmed === "" ? null : trimmed;
}

/** Notes as an old value: their text, empty being *None*. */
function oldNotes(html: string | null | undefined): OldValue {
  return oldValue(notesText(html));
}

/** The old value when the two sides differ; `undefined` when they are equal or both empty. */
function differing(old: string | null | undefined, now: string | null | undefined) {
  const before = oldValue(old);
  return before === oldValue(now) ? undefined : before;
}

/** A specific line's room list under change: every current room, then the removed ones. */
export interface RoomChanges {
  current: { name: string; added: boolean }[];
  removed: string[];
}

/** Whose picture the struck ghost above the current one shows. */
export type GhostPicture = "base-item" | "current-item" | "per-plan";

/**
 * The old values a row draws, each present only where a pair shows. `null`
 * is an empty old side (*None*); an empty **new** side is the drawing's to
 * read as *None*.
 */
export interface RowBasePairs {
  /** The name cell: a single row's whole combined name, a parent's line name, a child's selection name. */
  name?: OldValue;
  /** The old locations text, on a location type change. */
  locations?: string;
  /** The room list in place of the plain room text. */
  rooms?: RoomChanges;
  ghost?: GhostPicture;
  /** The item columns, each only where the two differ (D3: the maker on the details page only). */
  manufacturer?: OldValue;
  itemName?: OldValue;
  jde?: OldValue;
  model?: OldValue;
  /** A line turned per-plan: the per-plan cell's old side. */
  perPlan?: string;
  /** The per-plan selection's own manufacturer. */
  perPlanManufacturer?: OldValue;
  paidUpgrade?: "set" | "cleared";
  /** The notes the row's notes cell shows, as old text, uncut. */
  notes?: OldValue;
  /**
   * A one-selection row's own selection notes, drawn under the line's notes
   * though the donor never draws them there (#1106 D7 = b, a ruled parity break).
   */
  selectionNotes?: OldValue;
  /** The room notes under them pair, each from its placement (`roomNotesPair`). */
  roomNotes?: true;
  /** The line's removed rooms that had notes, after its rooms' notes (#1106 D8). */
  removedRoomNotes?: RemovedRoomNote[];
}

/** A room the line no longer has, and its notes' text (#1106 D8). */
export interface RemovedRoomNote {
  name: string;
  notes: string;
}

/** The item facts an old value reads. */
interface PairItem {
  name: string;
  jdeItemNumber: string | null;
  modelNumber: string | null;
  manufacturer: { name: string };
}

/** The line facts the pairs read. */
export interface LinePairFacts extends LineChangeFacts {
  name: string;
  locationType: LineLocationType;
  perPlan: boolean;
  locations: readonly { name: string; addedFromBase: boolean }[];
  base: {
    name: string;
    notes: string | null;
    locationType: LineLocationType;
    perPlan: boolean;
    removedRooms: readonly { name: string; notes: string | null }[];
  } | null;
}

/** The selection facts the pairs read. */
export interface SelectionPairFacts extends SelectionChangeFacts {
  name: string | null;
  isPaidUpgrade: boolean;
  item: PairItem | null;
  base: {
    name: string | null;
    notes: string | null;
    manufacturer: { name: string } | null;
    item: PairItem | null;
  } | null;
}

/** Which row of a line: one row, a parent's line row, or a child's selection row. */
export type PairRowKind = "single" | "parent" | "child";

/**
 * Whether a row is itself the change — struck, or added — so it draws no
 * per-field pairs. A parent row is judged by its line alone.
 */
function isRowTheChange(
  line: LineChangeFacts,
  selection: SelectionChangeFacts | undefined,
  specType: SpecType,
  kind: PairRowKind,
): boolean {
  if (kind === "parent") return line.deleted || (specType === "community" && !line.hasBaseRow);
  return isStruckRow(line, selection) || isAddedRow(line, selection, specType);
}

/**
 * The old values a desktop row draws beside its new ones (ledger §7's
 * per-field table): none on a struck or added row, which is itself the
 * change; elsewhere only fields the server flagged, and among an item swap's
 * derived facts only those that differ.
 */
export function rowBasePairs(
  line: LinePairFacts & { notes: string | null },
  selection: (SelectionPairFacts & { notes: string | null }) | undefined,
  specType: SpecType,
  kind: PairRowKind,
): RowBasePairs {
  // A parent row is its line's own: its selections are judged on their rows.
  const rowSelection = kind === "parent" ? undefined : selection;
  if (isRowTheChange(line, selection, specType, kind)) return {};
  const pairs: RowBasePairs = {};
  const lineBase = line.changedFields.length > 0 ? line.base : null;
  const lineChanged = (field: LineChangedField) =>
    lineBase !== null && line.changedFields.includes(field);
  const selectionBase = rowSelection?.base ?? null;
  const selectionChanged = (field: SelectionChangedField) =>
    selectionBase !== null && Boolean(rowSelection?.changedFields.includes(field));

  // The name cell.
  if (kind === "single") {
    if (lineChanged("name") || selectionChanged("name")) {
      const now = lineDisplayName(line.name, selection?.name);
      const before = lineDisplayName(
        lineChanged("name") ? lineBase!.name : line.name,
        selectionChanged("name") ? selectionBase!.name : selection?.name,
      );
      if (before !== now) pairs.name = before;
    }
  } else if (kind === "parent") {
    if (lineChanged("name")) pairs.name = oldValue(lineBase!.name);
  } else if (selectionChanged("name")) {
    pairs.name = oldValue(selectionBase!.name);
  }

  // The locations line: the name cell's second line on a single and a parent row.
  if (kind !== "child") {
    if (lineChanged("locationType")) {
      pairs.locations = locationsText(lineBase!.locationType, lineBase!.removedRooms);
    }
    if (line.locationType === "specific" && (lineChanged("rooms") || lineChanged("locationType"))) {
      const rooms: RoomChanges = {
        current: line.locations.map((room) => ({ name: room.name, added: room.addedFromBase })),
        removed: lineChanged("rooms") ? lineBase!.removedRooms.map((room) => room.name) : [],
      };
      if (rooms.current.length === 0 && rooms.removed.length > 0) {
        // Every room gone: the base's rooms struck, ↳ "0 Specific locations",
        // as any other pair (disposition D5 on PR #1106).
        pairs.locations = locationsText("specific", lineBase!.removedRooms);
      } else if (rooms.current.length > 0 || rooms.removed.length > 0) {
        pairs.rooms = rooms;
      }
    }
  }

  // The parent row draws only its line's name, locations and notes.
  if (kind !== "parent") {
    const turned = lineChanged("perPlan");
    const swapped = selectionChanged("item");
    if (line.perPlan && turned) {
      pairs.perPlan = NOT_PER_PLAN_TEXT;
      if (swapped ? selectionBase!.item : selection?.item) {
        pairs.ghost = swapped ? "base-item" : "current-item";
      }
    } else if (!line.perPlan && turned) {
      pairs.ghost = "per-plan";
      pairs.itemName = PER_PLAN_TEXT;
    } else if (!line.perPlan && swapped) {
      const before = selectionBase!.item;
      const now = selection?.item ?? null;
      if (before) pairs.ghost = "base-item";
      const maker = differing(before?.manufacturer.name, now?.manufacturer.name);
      if (maker !== undefined) pairs.manufacturer = maker;
      const name = differing(before?.name, now?.name);
      if (name !== undefined) pairs.itemName = name;
      const jde = differing(before?.jdeItemNumber, now?.jdeItemNumber);
      if (jde !== undefined) pairs.jde = jde;
      const model = differing(before?.modelNumber, now?.modelNumber);
      if (model !== undefined) pairs.model = model;
    }
    if (selectionChanged("manufacturer")) {
      pairs.perPlanManufacturer = oldValue(selectionBase!.manufacturer?.name);
    }
    if (selectionChanged("paidUpgrade")) {
      pairs.paidUpgrade = selection!.isPaidUpgrade ? "set" : "cleared";
    }
  }

  // The notes the cell shows: a child row's own, else the line's — and on a
  // single row its one selection's under them (D7); and on a selection's row,
  // the line's room notes beneath, then each room it lost that had a note
  // (D8): a specific line's removed room, or every room of a line that
  // stopped being specific.
  if (kind !== "parent" && lineChanged("roomNotes")) pairs.roomNotes = true;
  if (kind === "child") {
    if (selectionChanged("notes")) pairs.notes = oldNotes(selectionBase!.notes);
  } else if (lineChanged("notes")) {
    pairs.notes = oldNotes(lineBase!.notes);
  }
  if (kind === "single" && selectionChanged("notes")) {
    pairs.selectionNotes = oldNotes(selectionBase!.notes);
  }
  if (kind !== "parent" && (lineChanged("rooms") || lineChanged("locationType"))) {
    const removed = lineBase!.removedRooms.flatMap((room) => {
      const notes = oldNotes(room.notes);
      return notes === null ? [] : [{ name: room.name, notes }];
    });
    if (removed.length > 0) pairs.removedRoomNotes = removed;
  }
  return pairs;
}

/**
 * A room's notes pair, from its placement's own facts: the base notes' text
 * (`null` — *None* — when the base room had none) where they changed, else
 * `undefined`.
 */
export function roomNotesPair(room: {
  notesChangedFromBase: boolean;
  baseNotes: string | null;
}): OldValue | undefined {
  return room.notesChangedFromBase ? oldNotes(room.baseNotes) : undefined;
}

/** A group header's pairs: its old name and old notes text, where flagged. */
export function groupBasePairs(group: GroupChangeFacts): { name?: OldValue; notes?: OldValue } {
  const { base } = group;
  if (!base) return {};
  return {
    ...(group.changedFields.includes("name") ? { name: oldValue(base.name) } : {}),
    ...(group.changedFields.includes("notes") ? { notes: oldNotes(base.notes) } : {}),
  };
}

// ---------------------------------------------------------------------------
// The Editor's rows (#1104): the same pairs, through the Editor's own cells.
// ---------------------------------------------------------------------------

/** An Editor row as the pairs read it: a header row is its line's, a selection row its own. */
const EDITOR_PAIR_KIND: Record<EditorRowKind, PairRowKind> = {
  LINE_ONLY: "parent",
  LINE_AND_SELECTION: "single",
  SELECTION_ONLY: "child",
};

/** The Editor's per-plan text — `LinePerPlan`'s, with no period. */
export const EDITOR_PER_PLAN_TEXT = "Refer to plan for details";

/** The Editor's paid-upgrade badge text (`LineSelectionSummary`, `custom-badge`). */
export const EDITOR_PAID_UPGRADE_TEXT = "Paid Upgrade";

/** A newly set paid-upgrade mark's old side. */
export const NOT_PAID_UPGRADE_TEXT = "Not a paid upgrade";

/** A side the sentence reads as unchanged: the other side's row is judged alone. */
const UNCHANGED = { deleted: false, hasBaseRow: true, changedFields: [] } as const;

/**
 * The old values an Editor row draws in its cells: `rowBasePairs`' rule
 * through the Editor's cells (the #1043 brief's Editor column). Its names are
 * the Editor's — a single row's `editorLineName`, a selection row's
 * `selectionRowName`, each paired as one piece when any part changed; its JDE
 * is `#`-prefixed as the cell prints it; a line turned back from per-plan
 * strikes the Editor's per-plan text; and it draws no maker pair, as the
 * Editor shows no maker (D3). Notes pair in the notes rows
 * (`editorNotesPairs`), not here.
 */
export function editorRowBasePairs(
  line: LinePairFacts & { notes: string | null },
  selection: (SelectionPairFacts & { notes: string | null }) | undefined,
  specType: SpecType,
  kind: EditorRowKind,
): Omit<RowBasePairs, "manufacturer" | "notes" | "roomNotes"> {
  const pairKind = EDITOR_PAIR_KIND[kind];
  if (isRowTheChange(line, selection, specType, pairKind)) return {};
  // The Editor's cells: no maker (D3), and notes in the notes rows.
  const {
    locations,
    rooms,
    ghost,
    itemName,
    jde,
    model,
    perPlan,
    perPlanManufacturer,
    paidUpgrade,
  } = rowBasePairs(line, selection, specType, pairKind);
  const pairs: RowBasePairs = {
    locations,
    rooms,
    ghost,
    itemName,
    jde,
    model,
    perPlan,
    perPlanManufacturer,
    paidUpgrade,
  };

  const lineBase = line.changedFields.length > 0 ? line.base : null;
  const lineNameChanged = lineBase !== null && line.changedFields.includes("name");
  const selectionBase = selection?.base ?? null;
  const selectionChanged = (field: SelectionChangedField) =>
    selectionBase !== null && Boolean(selection?.changedFields.includes(field));

  const name = (() => {
    if (kind === "LINE_ONLY") return lineNameChanged ? oldValue(lineBase!.name) : undefined;
    if (kind === "LINE_AND_SELECTION") {
      if (!lineNameChanged && !selectionChanged("name")) return undefined;
      const before = editorLineName(
        kind,
        lineNameChanged ? lineBase!.name : line.name,
        selectionChanged("name") ? selectionBase!.name : selection?.name,
      );
      return before === editorLineName(kind, line.name, selection?.name) ? undefined : before;
    }
    if (!selection || (!selectionChanged("name") && !selectionChanged("paidUpgrade"))) {
      return undefined;
    }
    const before = selectionRowName({
      name: selectionChanged("name") ? selectionBase!.name : selection.name,
      isPaidUpgrade: selectionChanged("paidUpgrade")
        ? !selection.isPaidUpgrade
        : selection.isPaidUpgrade,
    });
    return before === selectionRowName(selection) ? undefined : oldValue(before);
  })();

  return {
    ...pairs,
    ...(name !== undefined ? { name } : {}),
    ...(pairs.ghost === "per-plan" ? { itemName: EDITOR_PER_PLAN_TEXT } : {}),
    ...(pairs.jde !== undefined ? { jde: pairs.jde === null ? null : `#${pairs.jde}` } : {}),
  };
}

/**
 * The info icon's sentence on an Editor row, by kind (`isEditorRowChanged`'s
 * facts): a header row names its line's fields, a single row the line's then
 * the selection's, a selection row its own — struck with its line, so a
 * deleted line's rows all read "Removed…", and added with it, so a line the
 * community added reads "Added…" on every row, as its pairs judge it
 * (`isRowTheChange`).
 */
export function editorRowChangeSentence(
  kind: EditorRowKind,
  line: LineChangeFacts,
  selection: SelectionChangeFacts | undefined,
  specType: SpecType,
): string | null {
  switch (kind) {
    case "LINE_ONLY":
      // Its line's fields alone: the header row stands over its selections,
      // and never reads as a line with none.
      return rowChangeSentence(line, UNCHANGED, specType);
    case "LINE_AND_SELECTION":
      return rowChangeSentence(line, selection, specType);
    case "SELECTION_ONLY":
      return rowChangeSentence(
        { ...UNCHANGED, deleted: line.deleted, hasBaseRow: line.hasBaseRow },
        selection,
        specType,
      );
  }
}

/**
 * The old notes an Editor notes row draws (#1104): a selection's notes row its
 * selection's notes, judged as its selection row is; the line's notes row its
 * line's notes, its removed rooms that had notes (#1106 D8), and — on a
 * specific line's table — whether its rooms' notes pair (`roomNotesPair`),
 * judged by the line alone, as its own row is. None on a struck or added row,
 * which is itself the change.
 */
export function editorNotesPairs(
  line: LinePairFacts & { notes: string | null },
  selection: (SelectionPairFacts & { notes: string | null }) | undefined,
  specType: SpecType,
): Pick<RowBasePairs, "notes" | "roomNotes"> & { removedRooms?: RemovedRoomNote[] } {
  if (selection) {
    const { notes } = rowBasePairs(line, selection, specType, "child");
    return notes === undefined ? {} : { notes };
  }
  if (isRowTheChange(line, undefined, specType, "parent")) return {};
  const lineBase = line.changedFields.length > 0 ? line.base : null;
  if (!lineBase) return {};
  const removedRooms = hasRemovedRoomNotes(line)
    ? lineBase.removedRooms.flatMap((room) => {
        const notes = oldNotes(room.notes);
        return notes === null ? [] : [{ name: room.name, notes }];
      })
    : [];
  return {
    ...(line.changedFields.includes("notes") ? { notes: oldNotes(lineBase.notes) } : {}),
    ...(line.changedFields.includes("roomNotes") ? { roomNotes: true as const } : {}),
    ...(removedRooms.length > 0 ? { removedRooms } : {}),
  };
}

/** A notes table's changes: the item's own note, and each room's by its placement id. */
export interface NotesTableChanges {
  item?: OldValue;
  rooms?: ReadonlyMap<number, OldValue>;
  /** The line's removed rooms that had notes, after its own (#1106 D8). */
  removedRooms?: readonly RemovedRoomNote[];
}

/**
 * The changes a specific line's room-notes table draws (#1104; #1060 Q9):
 * the line's own note where it changed, and — where the line's room notes
 * did — each changed placement's base notes (`roomNotesPair`). `undefined`
 * when nothing pairs.
 */
export function editorNotesTableChanges(
  line: Omit<LinePairFacts, "locations"> & {
    notes: string | null;
    locations: readonly (LinePairFacts["locations"][number] & {
      id: number;
      notesChangedFromBase: boolean;
      baseNotes: string | null;
    })[];
  },
  specType: SpecType,
): NotesTableChanges | undefined {
  const pairs = editorNotesPairs(line, undefined, specType);
  const rooms = pairs.roomNotes
    ? new Map(
        line.locations.flatMap((room) => {
          const old = roomNotesPair(room);
          return old === undefined ? [] : [[room.id, old] as const];
        }),
      )
    : undefined;
  const changes: NotesTableChanges = {
    ...(pairs.notes !== undefined ? { item: pairs.notes } : {}),
    ...(rooms && rooms.size > 0 ? { rooms } : {}),
    ...(pairs.removedRooms ? { removedRooms: pairs.removedRooms } : {}),
  };
  return Object.keys(changes).length > 0 ? changes : undefined;
}
